import { ListCountEstimateMode, ListCountRequestMode, OneOrManySchema } from 'notable-common';
import { z } from 'zod';
import { FlowDefinitionTemplateListItemSchema } from './flowDefinitionTemplate.js';
import { FlowDefinitionTemplateVisibility } from './visibility.js';
import { FlowDefinitionState } from '../flowDefinition/flowDefinition.js';
/**
 * The template library is browsed as a whole — the staff picker filters and searches the result in
 * the browser — so the default page is large enough to serve that while still bounding a caller
 * that asks for nothing.
 */
export const FLOW_DEFINITION_TEMPLATE_LIST_DEFAULT_LIMIT = 10_000;
export const FLOW_DEFINITION_TEMPLATE_LIST_MAX_LIMIT = 10_000;
export var FlowDefinitionTemplateOrderByField;
(function (FlowDefinitionTemplateOrderByField) {
    FlowDefinitionTemplateOrderByField["Name"] = "name";
    FlowDefinitionTemplateOrderByField["Created"] = "created";
    FlowDefinitionTemplateOrderByField["Updated"] = "updated";
})(FlowDefinitionTemplateOrderByField || (FlowDefinitionTemplateOrderByField = {}));
/** Whose templates to return, rather than a boolean whose meaning depends on the reader. */
export var FlowDefinitionTemplateCreatorFilter;
(function (FlowDefinitionTemplateCreatorFilter) {
    FlowDefinitionTemplateCreatorFilter["Anyone"] = "Anyone";
    FlowDefinitionTemplateCreatorFilter["CurrentUser"] = "CurrentUser";
})(FlowDefinitionTemplateCreatorFilter || (FlowDefinitionTemplateCreatorFilter = {}));
export var FlowDefinitionTemplateBookmarkFilter;
(function (FlowDefinitionTemplateBookmarkFilter) {
    FlowDefinitionTemplateBookmarkFilter["Any"] = "Any";
    FlowDefinitionTemplateBookmarkFilter["BookmarkedByCurrentUser"] = "BookmarkedByCurrentUser";
})(FlowDefinitionTemplateBookmarkFilter || (FlowDefinitionTemplateBookmarkFilter = {}));
export const FlowDefinitionTemplateTagsQuerySchema = z.object({
    operator: z.enum(['AND', 'OR']),
    tags: z.array(z.object({ name: z.string(), category: z.string() })),
});
export const FlowDefinitionTemplateListQuerySchema = z
    .object({
    id: OneOrManySchema(z.uuid()).optional(),
    organizationId: OneOrManySchema(z.uuid()).optional(),
    nameSearch: z
        .string()
        .min(3)
        .max(200)
        .describe('Case-insensitive substring of the template name.')
        .optional(),
    visibility: OneOrManySchema(z.enum(FlowDefinitionTemplateVisibility)).optional(),
    state: z
        .enum(FlowDefinitionState)
        .describe('Publication state of the template definition. A caller without a template-management permission only ever receives Published templates, whatever this is set to.')
        .optional(),
    tags: z
        .string()
        .max(4_096)
        .describe('Tag filter as a JSON object: {"operator":"AND"|"OR","tags":[{"name":string,"category":string}]}. AND requires every tag, OR any of them.')
        .optional(),
    createdBy: z
        .enum(FlowDefinitionTemplateCreatorFilter)
        .describe('Restrict to templates created by the requesting user.')
        .optional()
        .default(FlowDefinitionTemplateCreatorFilter.Anyone),
    bookmarked: z
        .enum(FlowDefinitionTemplateBookmarkFilter)
        .describe('Restrict to templates the requesting user has bookmarked.')
        .optional()
        .default(FlowDefinitionTemplateBookmarkFilter.Any),
    limit: z.coerce
        .number()
        .int()
        .positive()
        .max(FLOW_DEFINITION_TEMPLATE_LIST_MAX_LIMIT)
        .optional()
        .default(FLOW_DEFINITION_TEMPLATE_LIST_DEFAULT_LIMIT),
    offset: z.coerce.number().int().nonnegative().optional(),
    orderBy: z
        .enum(FlowDefinitionTemplateOrderByField)
        .describe('Defaults to name. Results are always ordered, with a unique tie-breaker, so that offset paging is stable.')
        .optional(),
    orderDirection: z.enum(['asc', 'desc']).optional(),
    countMode: z
        .enum(ListCountRequestMode)
        .describe('None omits the count. Exact scans every match. Estimated scans at most 4,097 matches and may return a capped lower estimate when more than 4,096 match.')
        .optional()
        .default(ListCountRequestMode.None),
})
    .strict();
export const FlowDefinitionTemplateListResponseSchema = z.object({
    flowDefinitionTemplates: z.array(FlowDefinitionTemplateListItemSchema),
    paginationSummary: z
        .object({
        total: z.number().int().nonnegative(),
        status: z.enum(ListCountEstimateMode),
    })
        .describe('Match count and its accuracy status, present only when countMode asked for one. When status is Estimated, total is a capped lower estimate.')
        .optional(),
});
//# sourceMappingURL=list.js.map